"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { Gamja_Flower } from "next/font/google";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getAttendedSlugs } from "@/lib/server-actions/attendanceActions";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { usePastExamCompletion } from "@/hooks/usePastExamCompletion";
import RoundCompletionBadge from "@/components/past-exam/RoundCompletionBadge";
import { getPostImage, getScopeImage } from "@/lib/postImageUtils";
import { getTrailHref } from "@/lib/article-trail";

const gamjaFlower = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

interface Post {
  title: string;
  href: string;
  scope: string;
  updated: string;
  priority: string;
  essential?: boolean;
}

interface FolderGridProps {
  /** 기본 헤딩("{subjectName} 과목 - 기출 문제 포함 글")용. title 지정 시 무시. */
  subjectName?: string;
  posts: Post[];
  recommendedHrefs?: string[];
  children?: React.ReactNode;
  /**
   * 커스텀 상단 헤딩 노드. 미지정 시 "{subjectName} 과목 - 기출 문제 포함 글" 기본 헤딩 렌더.
   * 과목 글 목록이 아닌 다른 그룹핑(예: past-exam 연도별 회차, JCG-2164)에 재사용할 때 헤딩을 갈아끼운다.
   */
  title?: React.ReactNode;
  /**
   * scope(폴더 키) → 해당 폴더 모달의 DialogHeader 아래·아이콘 그리드 위에 렌더할 배너 노드 맵.
   * 키가 있는 scope의 모달에서만 배너가 노출된다(past-exam 연도별 후기 배너, JCG-2179).
   * 키가 없는 scope·미지정 시 배너 미표시 → 과목 글 등 기존 폴더 모달은 영향 없음.
   */
  modalBannerByScope?: Record<string, React.ReactNode>;
  /**
   * true이면 각 회차 카드 href에서 roundId를 뽑아 "기출 회차 완주 횟수" 뱃지를 표시한다(JCG-2242).
   * past-exam 폴더 그리드 전용 — 미지정(기본 false)이면 과목 글 등 다른 그리드는 영향 없음.
   * 로그인 사용자만 조회하며, 출석 뱃지와 별개 데이터/표시다.
   */
  enableCompletion?: boolean;
  /** 과목 지도 컨텍스트. 공통 글 링크가 과목별 학습 경로를 유지하도록 사용한다(JCG-2487). */
  trailCategoryPath?: string;
}

function groupPostsByScope(posts: Post[]): Map<string, Post[]> {
  const grouped = new Map<string, Post[]>();
  for (const post of posts) {
    const existing = grouped.get(post.scope);
    if (existing) {
      existing.push(post);
    } else {
      grouped.set(post.scope, [post]);
    }
  }
  return grouped;
}

export default function FolderGrid({
  subjectName,
  posts,
  recommendedHrefs = [],
  children,
  title,
  modalBannerByScope,
  enableCompletion = false,
  trailCategoryPath,
}: FolderGridProps) {
  const groupedPosts = groupPostsByScope(posts);
  const recommendedSet = new Set(recommendedHrefs);
  const [attendedPaths, setAttendedPaths] = useState<Set<string>>(new Set());
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  const { isPremiumOrAbove } = useEffectivePass();
  // past-exam 그리드(enableCompletion)는 출석 대신 완주를 표시하므로 출석 뱃지를 끈다. JCG-2242
  const showAttendance = isPremiumOrAbove && !enableCompletion;
  // 완주 횟수 로드(프리미엄 이상 + past-exam). completionForHref(href) → 횟수. JCG-2242
  const { showCompletion, completionForHref } =
    usePastExamCompletion(enableCompletion);

  useEffect(() => {
    // 출석은 과목 그리드 전용 — past-exam(enableCompletion)에선 조회/표시하지 않는다.
    if (!showAttendance) return;
    async function loadAttendance() {
      try {
        const slugs = await getAttendedSlugs();
        setAttendedPaths(new Set(slugs.map((s) => `/${s}`)));
      } catch {
        // 비로그인 사용자 등 에러 무시
      }
    }
    loadAttendance();
  }, [showAttendance]);

  const handleFolderClick = useCallback((scope: string) => {
    setOpenFolder(scope);
  }, []);

  const folders = Array.from(groupedPosts.entries());
  const openFolderPosts = openFolder ? groupedPosts.get(openFolder) : null;
  const openFolderBanner = openFolder ? modalBannerByScope?.[openFolder] : null;

  return (
    <div className="mt-8">
      {title !== undefined ? (
        title
      ) : (
        <h2
          className={`text-center text-xl font-semibold ${gamjaFlower.className} mb-4`}
        >
          {subjectName} 과목 -{" "}
          <span className="bg-amber-100 dark:bg-amber-900">기출 문제 포함</span>{" "}
          글
        </h2>
      )}

      {children}

      {/* 폴더 아이콘 그리드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 sm:gap-5">
        {folders.map(([scope, scopePosts]) => {
          const attendedCount = scopePosts.filter((p) =>
            attendedPaths.has(p.href)
          ).length;
          const unattendedCount = scopePosts.length - attendedCount;
          const hasRecommended = scopePosts.some((p) =>
            recommendedSet.has(p.href)
          );
          // 이 폴더(연도) 안에서 1회 이상 완주한 회차 수. JCG-2242
          const completedRoundCount = enableCompletion
            ? scopePosts.filter((p) => completionForHref(p.href) > 0).length
            : 0;

          return (
            <FolderIcon
              key={scope}
              scope={scope}
              posts={scopePosts}
              isOpen={openFolder === scope}
              attendedCount={attendedCount}
              unattendedCount={unattendedCount}
              hasRecommended={hasRecommended}
              showAttendance={showAttendance}
              showCompletion={showCompletion}
              completedRoundCount={completedRoundCount}
              onClick={() => handleFolderClick(scope)}
            />
          );
        })}
      </div>

      {/* 폴더 모달 */}
      <Dialog
        open={openFolder !== null}
        onOpenChange={(open) => {
          if (!open) setOpenFolder(null);
        }}
      >
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-xl">{openFolder}</DialogTitle>
          </DialogHeader>
          {/* 모달 상단 배너 — 해당 scope에 배너가 있을 때만(past-exam 연도별 후기, JCG-2179) */}
          {openFolderBanner && <div className="pt-2">{openFolderBanner}</div>}
          {openFolderPosts && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 pt-2">
              {openFolderPosts.map((post) => {
                const completionCount = enableCompletion
                  ? completionForHref(post.href)
                  : 0;
                return (
                  <PostIcon
                    key={post.href}
                    post={post}
                    trailCategoryPath={trailCategoryPath}
                    attended={showAttendance && attendedPaths.has(post.href)}
                    showAttendance={showAttendance}
                    showCompletion={showCompletion}
                    recommended={recommendedSet.has(post.href)}
                    completionCount={completionCount}
                  />
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** 아이폰 스타일 폴더 아이콘 */
function FolderIcon({
  scope,
  posts,
  isOpen,
  attendedCount,
  unattendedCount,
  hasRecommended,
  showAttendance,
  showCompletion,
  completedRoundCount,
  onClick,
}: {
  scope: string;
  posts: Post[];
  isOpen: boolean;
  attendedCount: number;
  unattendedCount: number;
  hasRecommended: boolean;
  showAttendance: boolean;
  showCompletion: boolean;
  completedRoundCount: number;
  onClick: () => void;
}) {
  const previewPosts = posts.slice(0, 4);

  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center group"
      aria-expanded={isOpen}
    >
      <div
        className={`relative w-full aspect-square rounded-[22%] p-[10%] transition-all duration-200 ${
          isOpen
            ? "bg-amber-100 dark:bg-amber-900/80 shadow-lg scale-105 ring-2 ring-amber-300 dark:ring-amber-700"
            : "bg-muted/70 dark:bg-muted/50 group-hover:bg-muted group-hover:shadow-md group-hover:scale-[1.03]"
        }`}
      >
        {/* 2x2 미니 감자 캐릭터 그리드 (개별 글 이미지 사용) */}
        <div className="grid grid-cols-2 gap-[6%] w-full h-full">
          {previewPosts.map((post, i) => (
            <MiniPostImage key={i} href={post.href} scope={scope} />
          ))}
          {Array.from({ length: Math.max(0, 4 - previewPosts.length) }).map(
            (_, i) => (
              <div
                key={`empty-${i}`}
                className="rounded-[18%] bg-background/40 dark:bg-background/20"
              />
            )
          )}
        </div>

        {/* 뱃지 영역 (우측 상단). showCompletion 일부 완주 시 🏆 N + ! M 두 pill이 나란히
            놓이므로, 우측 고정·좌측 성장 클러스터가 좁은 셀(md grid-cols-5)에서 카드 좌측이나
            좌상단 추천 뱃지를 넘지 않도록 justify-end + max-w 가드를 둔다(PostIcon 클러스터와 동일). JCG-2242 */}
        <div className="absolute -top-1 -right-1 flex items-center justify-end gap-0.5 max-w-[calc(100%-1rem)]">
          {showCompletion ? (
            /* past-exam(프리미엄): 출석 대신 완주 표시. JCG-2242
               - 전체 회차 완주: 🏆 (숫자 없이 트로피만)
               - 일부 완주: 🏆 N(완주 수) + 빨간 ! M(미완주 수)
               - 0개 완주: 빨간 ! M(전체 회차)만 */
            (() => {
              const totalRounds = posts.length;
              const notCompletedCount = totalRounds - completedRoundCount;
              const allCompleted =
                completedRoundCount > 0 && notCompletedCount === 0;
              return (
                <>
                  {completedRoundCount > 0 &&
                    (allCompleted ? (
                      /* 전체 완주: 크게 + 글로우 펄스 + 회전 테두리 빛(레이어 구조). JCG-2242 */
                      <div
                        className="completion-allclear relative min-w-7 h-7 rounded-full flex items-center justify-center"
                        role="img"
                        aria-label="전체 회차 완주"
                      >
                        {/* 회전 골드 테두리 빛. JCG-2242 */}
                        <span
                          className="completion-allclear__ring"
                          aria-hidden="true"
                        />
                        <span className="completion-allclear__core">
                          <span
                            className="relative z-10 text-[16px] leading-none select-none drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]"
                            aria-hidden="true"
                          >
                            🏆
                          </span>
                        </span>
                      </div>
                    ) : (
                      <div
                        className="min-w-5 h-5 px-1 bg-gradient-to-b from-violet-600 to-indigo-600 rounded-full flex items-center justify-center gap-0.5 shadow-sm ring-2 ring-background whitespace-nowrap"
                        role="img"
                        aria-label={`${completedRoundCount}개 회차 완주`}
                      >
                        <span className="text-[9px] leading-none select-none" aria-hidden="true">
                          🏆
                        </span>
                        <span className="text-[10px] font-bold text-white leading-none select-none tracking-tight">
                          {completedRoundCount}
                        </span>
                      </div>
                    ))}
                  {notCompletedCount > 0 && (
                    <div
                      className="min-w-5 h-5 px-1 bg-red-500 rounded-full flex items-center justify-center gap-0.5 shadow-sm ring-2 ring-background whitespace-nowrap"
                      role="img"
                      aria-label={`${notCompletedCount}개 회차 미완주`}
                    >
                      <span className="text-[10px] font-bold text-white leading-none select-none" aria-hidden="true">
                        !
                      </span>
                      <span className="text-[10px] font-bold text-white leading-none select-none tracking-tight">
                        {notCompletedCount}
                      </span>
                    </div>
                  )}
                </>
              );
            })()
          ) : showAttendance ? (
            <>
              {/* 출석 완료 뱃지 (녹색) */}
              {attendedCount > 0 && (
                <div className="min-w-5 h-5 px-1 bg-green-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
                  <span className="text-[10px] font-bold text-white leading-none">
                    {attendedCount}
                  </span>
                </div>
              )}
              {/* 미출석 뱃지 (붉은색) */}
              {unattendedCount > 0 && (
                <div className="min-w-5 h-5 px-1 bg-red-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
                  <span className="text-[10px] font-bold text-white leading-none">
                    {unattendedCount}
                  </span>
                </div>
              )}
            </>
          ) : (
            /* 비프리미엄: 글 수만 표시 */
            <div className="min-w-5 h-5 px-1 bg-amber-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
              <span className="text-[10px] font-bold text-white leading-none">
                {posts.length}
              </span>
            </div>
          )}
        </div>

        {/* 추천 뱃지 */}
        {hasRecommended && (
          <div className="absolute -top-1 -left-1 min-w-5 h-5 px-1 bg-amber-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
            <span className="text-[10px] font-bold text-white leading-none">
              추천
            </span>
          </div>
        )}
      </div>

      {/* 폴더 이름 */}
      <span className="mt-1.5 text-sm sm:text-base text-center font-medium leading-tight line-clamp-1 w-full">
        {scope}
      </span>
    </button>
  );
}

/** 개별 포스트 아이콘 - 감자 캐릭터 + 중요도 상단 배치 */
function PostIcon({
  post,
  attended,
  showAttendance,
  showCompletion = false,
  recommended,
  completionCount = 0,
  trailCategoryPath,
}: {
  post: Post;
  trailCategoryPath?: string;
  attended: boolean;
  showAttendance: boolean;
  /** past-exam(프리미엄): 출석 대신 완주 뱃지 표시. JCG-2242 */
  showCompletion?: boolean;
  recommended: boolean;
  /** 이 회차 완주 횟수 (0이면 미표시). JCG-2242 */
  completionCount?: number;
}) {
  const [imgSrc, setImgSrc] = useState(getPostImage(post.href));
  const scopeImage = getScopeImage(post.scope);
  const href = getTrailHref(post.href, trailCategoryPath);

  return (
    <Link href={href} prefetch={false} className="flex flex-col items-center group">
      <div className="relative w-full aspect-square">
        {/* 이미지 영역 (clip용) */}
        <div
          className={`absolute inset-0 rounded-[22%] border overflow-hidden transition-all duration-200 group-hover:shadow-md group-hover:scale-[1.04] ${
            recommended
              ? "border-amber-200 dark:border-amber-800"
              : "group-hover:border-muted-foreground/30"
          }`}
        >
          <Image
            src={imgSrc}
            alt={post.title}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 25vw, 20vw"
            onError={() => setImgSrc(scopeImage)}
          />
        </div>

        {/* 필수 뱃지 — 좌측 상단 */}
        {post.essential && (
          <div className="absolute -top-1 -left-1 min-w-5 h-5 px-1 bg-red-500 dark:bg-red-600 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background z-10">
            <span className="text-[10px] font-bold text-white leading-none select-none">필수</span>
          </div>
        )}

        {/* 뱃지 영역 (이미지 바깥, 잘리지 않음). 우측 상단 클러스터: 중요도 + 완주 pill.
            max-w 가드로 작은 셀(모달 grid-cols-3)에서 2자리+ 라벨이 카드 좌측을 넘지 않게 한다. JCG-2242 */}
        <div className="absolute -top-1 -right-1 flex items-center justify-end gap-0.5 z-10 max-w-[calc(100%+0.5rem)]">
          {/* 중요도 뱃지 — priority가 빈 문자열이면 렌더하지 않음 (기초 입문 글 등) */}
          {post.priority && (
            <div className="min-w-5 h-5 bg-background/90 dark:bg-background/80 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background text-sm leading-none">
              <span className="select-none">{post.priority}</span>
            </div>
          )}
          {/* 출석 여부 뱃지 (과목 그리드, 프리미엄 이상만) */}
          {showAttendance && (
            attended ? (
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            ) : (
              <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-sm ring-2 ring-background">
                <span className="text-[10px] font-bold text-white leading-none">!</span>
              </div>
            )
          )}
          {/* 완주 뱃지 — past-exam(프리미엄), 출석 자리에 표시. 완주 시 🏆 N회, 미완주 시 빨간 !.
              FolderGrid·PastExamIconGrid 공유 컴포넌트로 회차 카드 어디서나 동일. JCG-2242 */}
          {showCompletion && (
            <RoundCompletionBadge completionCount={completionCount} />
          )}
        </div>
      </div>
      <span className="mt-1.5 text-[11px] sm:text-xs text-center line-clamp-2 w-full leading-tight text-muted-foreground group-hover:text-foreground transition-colors">
        {post.title}
      </span>
    </Link>
  );
}

/** 폴더 미니 그리드용 이미지 (개별 글 이미지 → scope 폴백) */
function MiniPostImage({ href, scope }: { href: string; scope: string }) {
  const [imgSrc, setImgSrc] = useState(getPostImage(href));
  const scopeImage = getScopeImage(scope);

  return (
    <div className="relative rounded-[18%] overflow-hidden bg-background/80 dark:bg-background/40">
      <Image
        src={imgSrc}
        alt={scope}
        fill
        className="object-cover"
        sizes="(max-width: 640px) 20vw, 10vw"
        onError={() => setImgSrc(scopeImage)}
      />
    </div>
  );
}
